import React, { useEffect, useState } from "react";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Link, useParams } from "react-router-dom";

const API_TOKEN = process.env.REACT_APP_API_TOKEN;

function OpenStudio() {
  const { slug } = useParams();

  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [access, setAccess] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadStudio = async () => {
      setLoading(true);
      setLoadError("");
      setFormError("");
      setStudio(null);
      setAccess(null);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_FOLDER}get_openstudio/?slug=${encodeURIComponent(slug)}`,
          { headers: { "X-API-TOKEN": API_TOKEN } }
        );

        if (cancelled) return;

        if (response.data && response.data.error) {
          setLoadError(response.data.error);
          return;
        }

        setStudio(response.data);

        // launch_os redirects replay/processing visitors back here with the secure
        // token in the URL fragment. Fragments are not sent as HTTP referrers.
        const hash = window.location.hash.replace(/^#/, "");
        const hashParams = new URLSearchParams(hash);
        const accessToken = hashParams.get("os_access");

        if (accessToken) {
          const payload = new URLSearchParams();
          payload.append("slug", slug);
          payload.append("token", accessToken);

          try {
            const accessResponse = await axios.post(
              `${process.env.REACT_APP_API_FOLDER}get_openstudio_access/`,
              payload,
              {
                headers: {
                  "X-API-TOKEN": API_TOKEN,
                  "Content-Type": "application/x-www-form-urlencoded",
                },
              }
            );

            if (cancelled) return;

            if (
              accessResponse.data &&
              accessResponse.data.success === true &&
              accessResponse.data.access === true
            ) {
              setAccess(accessResponse.data);
            } else {
              setFormError(
                (accessResponse.data && accessResponse.data.error) ||
                  "We couldn't verify this Open Studio access link."
              );
            }
          } catch (accessError) {
            if (!cancelled) {
              const responseMessage =
                accessError.response &&
                accessError.response.data &&
                accessError.response.data.error;
              setFormError(
                responseMessage ||
                  accessError.message ||
                  "We couldn't verify this Open Studio access link."
              );
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error.message || "This Open Studio could not be loaded.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadStudio();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const submitAccess = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!firstName.trim() || !email.trim()) {
      setFormError("Enter your first name and email address.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = new URLSearchParams();
      payload.append("slug", slug);
      payload.append("first_name", firstName.trim());
      payload.append("email", email.trim());
      payload.append("newsletter_optin", "1");
      payload.append("website", website);

      const response = await axios.post(
        `${process.env.REACT_APP_API_FOLDER}register_openstudio/`,
        payload,
        {
          headers: {
            "X-API-TOKEN": API_TOKEN,
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (!response.data || response.data.success !== true || !response.data.access) {
        setFormError(
          (response.data && response.data.error) ||
            "We couldn't send your access link. Please try again."
        );
        return;
      }

      setAccess(response.data);
    } catch (error) {
      const responseMessage =
        error.response && error.response.data && error.response.data.error;

      setFormError(
        responseMessage ||
          error.message ||
          "We couldn't send your access link. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section className="openstudio-page section-bottom-pad">
        <div className="package-details-wrapper pt-110">
          <div className="container">
            <Skeleton height={60} />
            <div className="mt-30">
              <Skeleton height={440} />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (loadError || !studio) {
    return (
      <section className="openstudio-page section-bottom-pad">
        <div className="package-details-wrapper pt-110">
          <div className="container">
            <h2>Open Studio unavailable</h2>
            <p>{loadError || "This Open Studio could not be found."}</p>
            <Link to="/openstudios">← Open Studios</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="openstudio-page section-bottom-pad">
      <div className="package-details-wrapper pt-110">
        <div className="container">
          <Link
            className="openstudio-back-link"
            to="/openstudios"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <i className="bi bi-arrow-left" aria-hidden="true" /> Open Studios
          </Link>

          <div className="openstudio-hero">
            <div className="openstudio-hero-image">
              {studio.image ? (
                <img src={studio.image} alt={`${studio.title} with ${studio.presenter}`} />
              ) : (
                <div className="openstudio-hero-placeholder" aria-hidden="true" />
              )}
            </div>

            <div className="openstudio-hero-copy">
              <p className="openstudio-kicker">CAMP Open Studio</p>
              <h1>{studio.title}</h1>
              <p className="openstudio-presenter">with {studio.presenter}</p>

              <div className="openstudio-schedule">
                <div>
                  <i className="bi bi-calendar-event" aria-hidden="true" />
                  {studio.date_label}
                </div>
                <div>
                  <i className="bi bi-clock" aria-hidden="true" />
                  {studio.time_label} {studio.timezone_label}
                </div>
                <div>
                  <i className="bi bi-camera-video" aria-hidden="true" />
                  Online · free
                </div>
              </div>

              <div className="openstudio-description">{studio.description}</div>

              {studio.registration_open && !access && (
                <div className="openstudio-access-card">
                  <h2>
                    {studio.access_mode === "live"
                      ? "Join Open Studio"
                      : "Watch Open Studio"}
                  </h2>
                  <p>
                    {studio.access_mode === "live"
                      ? "Enter your details below and we'll email you a secure link to join this Open Studio. The Zoom link is only released through that email, so please use an address you can access."
                      : studio.access_mode === "replay"
                        ? "Enter your details below and we'll email you a secure link to watch the Open Studio recording."
                        : "Enter your details below and we'll email you a secure access link. The recording is still being processed; the same link will work when it is ready."}
                  </p>

                  <form onSubmit={submitAccess}>
                    <div className="openstudio-form-row">
                      <div>
                        <label htmlFor="openstudio-first-name">First name</label>
                        <input
                          id="openstudio-first-name"
                          type="text"
                          autoComplete="given-name"
                          maxLength="100"
                          required
                          value={firstName}
                          onChange={(event) => setFirstName(event.target.value)}
                        />
                      </div>

                      <div>
                        <label htmlFor="openstudio-email">Email</label>
                        <input
                          id="openstudio-email"
                          type="email"
                          autoComplete="email"
                          maxLength="255"
                          required
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                        />
                      </div>
                    </div>

                    <div className="openstudio-honeypot" aria-hidden="true">
                      <label htmlFor="openstudio-website">Website</label>
                      <input
                        id="openstudio-website"
                        type="text"
                        tabIndex="-1"
                        autoComplete="off"
                        value={website}
                        onChange={(event) => setWebsite(event.target.value)}
                      />
                    </div>

                    <p className="openstudio-privacy">
                      Your email address is required so we can send and verify your Open Studio access link.
                      By requesting an access link you'll also receive future CAMP Open Studios, workshops,
                      Field Notes and occasional CAMP news. You can unsubscribe at any time.{" "}
                      <Link to="/privacy">Privacy policy</Link>.
                    </p>

                    {formError && (
                      <div className="openstudio-form-error" role="alert">
                        {formError}
                      </div>
                    )}

                    <button type="submit" disabled={submitting}>
                      {submitting ? "Sending…" : "Email access link"}
                    </button>
                  </form>
                </div>
              )}

              {access && (
                <div className="openstudio-access-card openstudio-access-success">
                  {access.access_type === "email" && (
                    <>
                      <h2>Check your email</h2>
                      <p>
                        We've emailed your Open Studio access link to{" "}
                        <strong>{access.delivery_email}</strong>.
                      </p>
                      <p>
                        Use the link in that email to verify your address and access the Open Studio.
                      </p>
                    </>
                  )}

                  {access.access_type === "zoom" && access.zoom_url && (
                    <>
                      <h2>You're verified</h2>
                      <p>Your Open Studio access is ready.</p>
                      <a
                        className="openstudio-join-button"
                        href={access.zoom_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Join Open Studio on Zoom
                        <i className="bx bxs-right-arrow-alt" aria-hidden="true" />
                      </a>
                    </>
                  )}

                  {access.access_type === "vimeo" && access.vimeo_id && (
                    <>
                      <h2>Watch Open Studio</h2>
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          paddingTop: "56.25%",
                          overflow: "hidden",
                          borderRadius: "6px",
                          background: "#000",
                        }}
                      >
                        <iframe
                          src={`https://player.vimeo.com/video/${encodeURIComponent(
                            access.vimeo_id
                          )}`}
                          title={`${studio.title} with ${studio.presenter}`}
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          referrerPolicy="no-referrer"
                          style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            border: 0,
                          }}
                        />
                      </div>
                    </>
                  )}

                  {access.access_type === "processing" && (
                    <>
                      <h2>Recording processing</h2>
                      <p>
                        {access.message ||
                          "The recording is being processed. Please check back shortly."}
                      </p>
                    </>
                  )}

                  {access.newsletter_message && (
                    <p className="openstudio-newsletter-message">
                      {access.newsletter_message}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default OpenStudio;